import React, { useState, useEffect, useRef } from 'react';
import {
  ArrowLeft, Send, Loader2, Eye, EyeOff, Shield, MessageSquare,
  Upload, X, ChevronDown, Check, Plus, Users, Briefcase, Star,
  CheckCircle, Building2, Zap, Lock, Mic, Radio, Hash, MessageCircle,
} from 'lucide-react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import Navbar from '../components/Navbar';
import QRCode from 'qrcode';
import AuthPlatformNotice from '../components/AuthPlatformNotice';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const WORKDAY_MODULES = [
  { group: 'Core Modules', items: [
    { value: 'Human Capital Management (HCM)', label: 'Human Capital Management (HCM)' },
    { value: 'Payroll', label: 'Payroll' },
    { value: 'Recruiting', label: 'Recruiting' },
    { value: 'Time Tracking', label: 'Time Tracking' },
    { value: 'Absence', label: 'Absence' },
    { value: 'Adaptive Planning', label: 'Adaptive Planning' },
    { value: 'Prism Analytics', label: 'Prism Analytics' },
  ]},
  { group: 'Integrations & Document Management', items: [
    { value: 'Workday + DocuSign Integration', label: 'Workday + DocuSign Integration' },
    { value: 'Workday + Adobe (Document Generation & Management)', label: 'Workday + Adobe (Document Generation & Management)' },
  ]},
  { group: 'Delivery, Optimization & Best Practices', items: [
    { value: 'Workday Agile Change Management', label: 'Workday Agile Change Management' },
    { value: 'Best Practice Implementation', label: 'Best Practice Implementation' },
  ]},
  { group: 'Key Experience Highlights', items: [
    { value: 'End-to-End Implementations & Post-Production Support', label: 'End-to-End Implementations & Post-Production Support' },
    { value: 'Cross-Functional Stakeholder Collaboration', label: 'Cross-Functional Stakeholder Collaboration' },
    { value: 'User Training & Knowledge Transfer', label: 'User Training & Knowledge Transfer' },
    { value: 'Tiered System Support (L1–L3)', label: 'Tiered System Support (L1–L3)' },
    { value: 'Reporting & Analytics Optimization', label: 'Reporting & Analytics Optimization' },
    { value: 'Data Integrity & Compliance', label: 'Data Integrity & Compliance' },
  ]},
];

const PROJECT_PREFERENCES = [
  { group: 'Workday Position & Project — Core Delivery', items: [
    { value: 'End-to-End Implementations & Post-Production Support — Workday Position & Project', label: 'End-to-End Implementations & Post-Production Support — Workday Position & Project' },
    { value: 'Cross-Functional Stakeholder Collaboration — Workday Position & Project', label: 'Cross-Functional Stakeholder Collaboration — Workday Position & Project' },
    { value: 'User Training & Knowledge Transfer — Workday Position & Project', label: 'User Training & Knowledge Transfer — Workday Position & Project' },
    { value: 'Tiered System Support (L1–L3) — Workday Position & Project', label: 'Tiered System Support (L1–L3) — Workday Position & Project' },
    { value: 'Reporting & Analytics Optimization — Workday Position & Project', label: 'Reporting & Analytics Optimization — Workday Position & Project' },
    { value: 'Data Integrity & Compliance — Workday Position & Project', label: 'Data Integrity & Compliance — Workday Position & Project' },
  ]},
  { group: 'Workday Position & Project — Functional Expertise', items: [
    { value: 'Human Capital Management (HCM) — Workday Position & Project', label: 'Human Capital Management (HCM) — Workday Position & Project' },
    { value: 'Payroll — Workday Position & Project', label: 'Payroll — Workday Position & Project' },
    { value: 'Recruiting — Workday Position & Project', label: 'Recruiting — Workday Position & Project' },
    { value: 'Time Tracking — Workday Position & Project', label: 'Time Tracking — Workday Position & Project' },
    { value: 'Absence — Workday Position & Project', label: 'Absence — Workday Position & Project' },
    { value: 'Adaptive Planning — Workday Position & Project', label: 'Adaptive Planning — Workday Position & Project' },
    { value: 'Prism Analytics — Workday Position & Project', label: 'Prism Analytics — Workday Position & Project' },
  ]},
  { group: 'Workday Position & Project — Integrations & Documents', items: [
    { value: 'Workday + DocuSign Integration — Workday Position & Project', label: 'Workday + DocuSign Integration — Workday Position & Project' },
    { value: 'Workday + Adobe (Document Generation & Management) — Workday Position & Project', label: 'Workday + Adobe (Document Generation & Management) — Workday Position & Project' },
  ]},
  { group: 'Workday Position & Project — Delivery & Best Practices', items: [
    { value: 'Workday Agile Change Management — Workday Position & Project', label: 'Workday Agile Change Management — Workday Position & Project' },
    { value: 'Best Practice Implementation — Workday Position & Project', label: 'Best Practice Implementation — Workday Position & Project' },
  ]},
];

const US_STATES = [
  'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut','Delaware',
  'Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa','Kansas','Kentucky',
  'Louisiana','Maine','Maryland','Massachusetts','Michigan','Minnesota','Mississippi',
  'Missouri','Montana','Nebraska','Nevada','New Hampshire','New Jersey','New Mexico',
  'New York','North Carolina','North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania',
  'Rhode Island','South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont',
  'Virginia','Washington','West Virginia','Wisconsin','Wyoming','Washington D.C.'
];

const CANADIAN_PROVINCES = [
  'Alberta','British Columbia','Manitoba','New Brunswick','Newfoundland and Labrador',
  'Northwest Territories','Nova Scotia','Nunavut','Ontario','Prince Edward Island',
  'Quebec','Saskatchewan','Yukon'
];

const COUNTRIES = [
  'United States','Canada','United Kingdom','Australia','Germany','France','India',
  'Brazil','Mexico','South Africa','Nigeria','Kenya','Singapore','UAE','Other'
];

// ─────────────────────────────────────────────────────────────────────────────
// MultiSelectDropdown (consultant form only)
// ─────────────────────────────────────────────────────────────────────────────

interface MultiSelectDropdownProps {
  label: string;
  options: { group: string; items: { value: string; label: string }[] }[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  required?: boolean;
}

function MultiSelectDropdown({ label, options, selected, onChange, placeholder, required }: MultiSelectDropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (value: string) => {
    onChange(selected.includes(value) ? selected.filter(v => v !== value) : [...selected, value]);
  };

  const allItems = options.flatMap(g => g.items.map(i => i.value));
  const allSelected = allItems.every(v => selected.includes(v));

  return (
    <div ref={ref} className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {selected.map(v => (
            <span key={v} className="inline-flex items-center gap-1 text-xs bg-[#40E0D0]/15 text-[#1a9e90] border border-[#40E0D0]/40 rounded-full px-2.5 py-1 font-medium">
              {v}
              <button type="button" onClick={() => onChange(selected.filter(s => s !== v))} className="hover:text-red-500 transition-colors ml-0.5">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between bg-white border border-gray-300 rounded-md px-4 py-2.5 text-sm text-gray-700 hover:border-[#40E0D0] focus:outline-none focus:ring-2 focus:ring-[#40E0D0] transition-colors"
      >
        <span className={selected.length === 0 ? 'text-gray-400' : 'text-gray-700'}>
          {selected.length === 0 ? (placeholder || 'Select options...') : `${selected.length} selected`}
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-xl max-h-72 overflow-y-auto">
          <div className="sticky top-0 bg-white border-b border-gray-100 px-3 py-2">
            <button
              type="button"
              onClick={() => onChange(allSelected ? [] : allItems)}
              className="text-xs font-semibold text-[#40E0D0] hover:text-[#1a9e90] flex items-center gap-1 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              {allSelected ? 'Deselect All' : 'Select All'}
            </button>
          </div>
          {options.map(group => (
            <div key={group.group}>
              <div className="px-3 py-1.5 text-xs font-bold text-gray-400 uppercase tracking-wider bg-gray-50 border-b border-gray-100">
                {group.group}
              </div>
              {group.items.map(item => {
                const isSelected = selected.includes(item.value);
                return (
                  <button
                    key={item.value}
                    type="button"
                    onClick={() => toggle(item.value)}
                    className={`w-full text-left px-4 py-2.5 text-sm flex items-center justify-between hover:bg-[#40E0D0]/5 transition-colors ${isSelected ? 'text-[#1a9e90] font-medium' : 'text-gray-700'}`}
                  >
                    <span>{item.label}</span>
                    {isSelected && <Check className="w-4 h-4 text-[#40E0D0] flex-shrink-0" />}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

type UserType = 'consultant' | 'client';
type ClientPlan = 'free' | 'pro';

interface ConsultantForm {
  first_name: string; last_name: string; email: string; phone: string;
  company: string; company_status: string; title: string;
  zip_code: string; city: string; state: string; country: string;
  experience_years: string; workday_modules_list: string[];
  certifications: string; availability: string; hourly_rate: string;
  project_preferences_list: string[]; message: string;
  password: string; confirmPassword: string; resume_file: File | null;
}

interface ClientForm {
  first_name: string; last_name: string; email: string; phone: string;
  company: string; title: string; country_region: string;
  company_size: string; timeline: string; budget_range: string;
  password: string; confirmPassword: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// 2FA helpers
// ─────────────────────────────────────────────────────────────────────────────

function generate2FASecret() {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
  let secret = '';
  for (let i = 0; i < 32; i++) secret += chars.charAt(Math.floor(Math.random() * chars.length));
  return secret;
}

async function makeQrCode(secret: string, email: string) {
  const url = `otpauth://totp/PerplexityATS:${encodeURIComponent(email)}?secret=${secret}&issuer=PerplexityATS`;
  return QRCode.toDataURL(url);
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

const inputCls = 'mt-1 block w-full rounded-md bg-white border border-gray-300 text-gray-900 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent';

export default function ConsultantSignup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isConsultantOnly = searchParams.get('type') === 'consultant';

  // ── logo ───────────────────────────────────────────────────────────────────
  const [logoUrl, setLogoUrl] = useState('/perplexity-new-logo.png');

  useEffect(() => {
    supabase
      .from('settings')
      .select('value')
      .eq('key', 'logo_url')
      .single()
      .then(({ data, error }) => {
        if (data?.value && !error) {
          const img = new Image();
          img.onload = () => setLogoUrl(data.value);
          img.src = data.value;
        }
      });
  }, []);

  // ── user type + client plan selection ─────────────────────────────────────
  const [userType, setUserType] = useState<UserType>(isConsultantOnly ? 'consultant' : 'client');
  const [clientPlan, setClientPlan] = useState<ClientPlan>('free');

  // ── consultant form ────────────────────────────────────────────────────────
  const [cForm, setCForm] = useState<ConsultantForm>({
    first_name: '', last_name: '', email: '', phone: '',
    company: '', company_status: '', title: '',
    zip_code: '', city: '', state: '', country: 'United States',
    experience_years: '', workday_modules_list: [], certifications: '',
    availability: '', hourly_rate: '', project_preferences_list: [],
    message: '', password: '', confirmPassword: '', resume_file: null,
  });

  // ── client form ────────────────────────────────────────────────────────────
  const [clForm, setClForm] = useState<ClientForm>({
    first_name: '', last_name: '', email: '', phone: '',
    company: '', title: '', country_region: '',
    company_size: '', timeline: '', budget_range: '',
    password: '', confirmPassword: '',
  });

  // ── shared state ───────────────────────────────────────────────────────────
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [emailConflict, setEmailConflict] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // ── Pro plan confirmation modal ────────────────────────────────────────────
  const [showProConfirmModal, setShowProConfirmModal] = useState(false);
  // Stores validated form event so we can resume after confirm
  const pendingProSubmitRef = useRef<React.FormEvent | null>(null);

  // ── 2FA shared ─────────────────────────────────────────────────────────────
  const [show2FA, setShow2FA] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [twoFASecret, setTwoFASecret] = useState('');
  const [twoFACode, setTwoFACode] = useState('');
  const [twoFAError, setTwoFAError] = useState('');
  const [qrShownForEmail, setQrShownForEmail] = useState('');
  const [emailChangedAfterScan, setEmailChangedAfterScan] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  // reset 2FA when switching user type
  useEffect(() => {
    setShow2FA(false);
    setQrCodeUrl('');
    setTwoFASecret('');
    setTwoFACode('');
    setTwoFAError('');
    setQrShownForEmail('');
    setEmailChangedAfterScan(false);
    setFormError('');
    setEmailConflict(false);
    setSubmitStatus('idle');
  }, [userType]);

  const activeEmail = userType === 'consultant' ? cForm.email : clForm.email;

  // When email changes after QR was already shown: new secret + new QR + warn to re-scan
  useEffect(() => {
    if (!show2FA || !activeEmail || !qrShownForEmail) return;
    if (activeEmail === qrShownForEmail) return;

    const newSecret = generate2FASecret();
    setTwoFASecret(newSecret);
    setTwoFACode('');
    setEmailChangedAfterScan(true);
    makeQrCode(newSecret, activeEmail).then(url => {
      setQrCodeUrl(url);
      setQrShownForEmail(activeEmail);
      setEmailChangedAfterScan(false);
    });
  }, [activeEmail]);

  const handle2FASetup = async () => {
    setTwoFAError('');
    if (!activeEmail) {
      setTwoFAError('Please enter your email address before setting up Google Authenticator.');
      return;
    }
    try {
      const secret = generate2FASecret();
      setTwoFASecret(secret);
      const url = await makeQrCode(secret, activeEmail);
      setQrCodeUrl(url);
      setShow2FA(true);
      setQrShownForEmail(activeEmail);
      setEmailChangedAfterScan(false);
      setTwoFACode('');
    } catch {
      setTwoFAError('Failed to generate QR code. Please try again.');
    }
  };

  // ── consultant change handlers ─────────────────────────────────────────────
  const handleCChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setCForm(prev => ({ ...prev, [name]: value }));
    if (name === 'password' || name === 'confirmPassword') setFormError('');
    if (name === 'email') { setEmailConflict(false); setFormError(''); }
  };

  const formatPhone = (value: string) => {
    const d = value.replace(/\D/g, '');
    if (d.length <= 3) return d;
    if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6, 10)}`;
  };

  // ── client change handlers ─────────────────────────────────────────────────
  const handleClChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setClForm(prev => ({ ...prev, [name]: value }));
    if (name === 'password' || name === 'confirmPassword') setFormError('');
    if (name === 'email') { setEmailConflict(false); setFormError(''); }
  };

  // ── consultant submit ──────────────────────────────────────────────────────
  const handleConsultantSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormError('');

    if (cForm.password.length < 6) { setFormError('Password must be at least 6 characters long'); setIsSubmitting(false); return; }
    if (cForm.password !== cForm.confirmPassword) { setFormError('Passwords do not match'); setIsSubmitting(false); return; }
    if (cForm.workday_modules_list.length === 0) { setFormError('Please select at least one Workday module or skill.'); setIsSubmitting(false); return; }
    if (twoFASecret && !twoFACode) { setTwoFAError('Please enter the 6-digit code from your authenticator app.'); setIsSubmitting(false); return; }
    if (twoFACode && twoFACode.replace(/\s/g, '').length !== 6) { setTwoFAError('The code must be exactly 6 digits.'); setIsSubmitting(false); return; }

    try {
      const location = [cForm.city, cForm.state, cForm.zip_code, cForm.country].filter(Boolean).join(', ');
      const companyValue = cForm.company_status === 'available_public'
        ? 'Available for Work (Public)'
        : cForm.company_status === 'available_private'
        ? 'Available to Work (Private)'
        : cForm.company || null;

      const payload: Record<string, unknown> = {
        email: cForm.email, password: cForm.password,
        first_name: cForm.first_name, last_name: cForm.last_name,
        phone: cForm.phone, company: companyValue,
        company_status: cForm.company_status || null,
        title: cForm.title || null, zip_code: cForm.zip_code || null,
        city: cForm.city, state: cForm.state, country: cForm.country,
        country_region: location,
        experience_years: cForm.experience_years,
        workday_modules: cForm.workday_modules_list.join(', '),
        workday_modules_list: cForm.workday_modules_list,
        certifications: cForm.certifications || null,
        availability: cForm.availability, hourly_rate: cForm.hourly_rate,
        message: cForm.message,
        project_preferences_list: cForm.project_preferences_list,
      };
      if (twoFASecret) { payload.twofa_secret = twoFASecret; payload.twofa_code = twoFACode.replace(/\s/g, ''); }

      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/consultant-signup-complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}` },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json();
        if (err.code === 'EMAIL_ALREADY_REGISTERED' || res.status === 409) {
          setEmailConflict(true);
          setIsSubmitting(false);
          return;
        }
        const msg = err.error || 'Failed to complete signup';
        setFormError(msg);
        if (msg.toLowerCase().includes('authenticator') || msg.toLowerCase().includes('2fa') || msg.toLowerCase().includes('code')) setTwoFAError(msg);
        throw new Error(msg);
      }

      const result = await res.json();
      if (cForm.resume_file && result.user_id) {
        try {
          const ext = cForm.resume_file.name.split('.').pop();
          const fileName = `${result.user_id}/resume.${ext}`;
          await supabase.storage.from('resumes').upload(fileName, cForm.resume_file, { upsert: true });
          await supabase.from('consultant_profiles').update({ resume_url: fileName }).eq('id', result.user_id);
        } catch { /* non-fatal */ }
      }

      setSubmitStatus('success');
      setSubmittedEmail(cForm.email);
      setTimeout(() => navigate('/consultant/login', { state: { justSignedUp: true, email: cForm.email } }), 3000);
    } catch (err: any) {
      setSubmitStatus('error');
      if (err?.message) setFormError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── client submit ──────────────────────────────────────────────────────────
  const handleClientSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    // Validate before anything else (do NOT set isSubmitting yet)
    if (clForm.password.length < 6) { setFormError('Password must be at least 6 characters long'); return; }
    if (clForm.password !== clForm.confirmPassword) { setFormError('Passwords do not match'); return; }
    if (twoFASecret && !twoFACode) { setTwoFAError('Please enter the 6-digit code from your authenticator app.'); return; }
    if (twoFACode && twoFACode.replace(/\s/g, '').length !== 6) { setTwoFAError('The code must be exactly 6 digits.'); return; }

    // For pro plan: show confirmation modal instead of submitting immediately
    if (clientPlan === 'pro') {
      pendingProSubmitRef.current = e;
      setShowProConfirmModal(true);
      return;
    }

    await doClientSignup('free');
  };

  // Executes the actual signup API call with the given plan
  const doClientSignup = async (plan: ClientPlan) => {
    setIsSubmitting(true);
    setFormError('');

    try {
      const payload: Record<string, unknown> = {
        email: clForm.email, password: clForm.password,
        first_name: clForm.first_name, last_name: clForm.last_name,
        phone: clForm.phone, title: clForm.title,
        country_region: clForm.country_region, company: clForm.company,
        company_size: clForm.company_size, timeline: clForm.timeline,
        budget_range: clForm.budget_range,
        plan,
      };
      if (twoFASecret) { payload.twofa_secret = twoFASecret; payload.twofa_code = twoFACode.replace(/\s/g, ''); }

      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/client-signup-complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}` },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json();
        if (err.code === 'EMAIL_ALREADY_REGISTERED' || res.status === 409) {
          setEmailConflict(true);
          setIsSubmitting(false);
          return;
        }
        const msg = err.error || 'Failed to complete signup';
        setFormError(msg);
        if (msg.toLowerCase().includes('authenticator') || msg.toLowerCase().includes('2fa') || msg.toLowerCase().includes('code')) setTwoFAError(msg);
        throw new Error(msg);
      }

      const result = await res.json();
      const savedEmail = clForm.email;

      // Sign in the user so they have a session for the upgrade page
      if (plan === 'pro') {
        const { error: signInErr } = await supabase.auth.signInWithPassword({
          email: clForm.email,
          password: clForm.password,
        });
        if (!signInErr) {
          navigate('/workday-client/upgrade', { state: { justSignedUp: true, email: savedEmail } });
          return;
        }
      }

      navigate('/customer/login', { state: { justSignedUp: true, email: savedEmail, has2FA: result.has2FA } });
    } catch (err: any) {
      setSubmitStatus('error');
      if (err?.message) setFormError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Called when user confirms "Yes, create Pro account" in the modal
  const handleConfirmPro = async () => {
    setShowProConfirmModal(false);
    await doClientSignup('pro');
  };

  // Called when user picks "No, just create a free account" in the modal
  const handleConfirmFree = async () => {
    setShowProConfirmModal(false);
    setClientPlan('free');
    await doClientSignup('free');
  };

  const stateOptions = cForm.country === 'Canada' ? CANADIAN_PROVINCES : US_STATES;
  const showStateDropdown = cForm.country === 'United States' || cForm.country === 'Canada';

  // ─────────────────────────────────────────────────────────────────────────
  // 2FA block (shared between both forms)
  // ─────────────────────────────────────────────────────────────────────────
  const twoFABlock = (
    <div className="border-t border-gray-100 pt-5">
      <p className="text-sm font-semibold text-gray-800 mb-1">Two-Factor Authentication <span className="text-gray-400 font-normal text-xs">(Recommended)</span></p>
      <p className="text-xs text-gray-500 mb-3">Adds an extra layer of security to your account.</p>
      <button
        type="button"
        onClick={handle2FASetup}
        className="w-full bg-white border-2 border-[#40E0D0] text-gray-700 font-semibold px-6 py-3 rounded-lg inline-flex items-center justify-center hover:bg-gray-50 transition-colors shadow-sm"
      >
        <svg className="w-5 h-5 mr-3 flex-shrink-0" viewBox="0 0 48 48">
          <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
          <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
          <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
          <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
        </svg>
        {show2FA ? 'Regenerate QR Code' : 'Set Up Google Authenticator'}
      </button>

      {twoFAError && <p className="text-red-600 text-sm mt-2">{twoFAError}</p>}

      {show2FA && qrCodeUrl && (
        <div className="mt-4 p-5 bg-gradient-to-br from-blue-50 to-white border-2 border-[#40E0D0] rounded-lg">
          <h4 className="font-semibold text-lg mb-3 text-center text-gray-900">Scan with Google Authenticator</h4>

          {emailChangedAfterScan && (
            <div className="bg-red-50 border border-red-300 text-red-700 rounded-lg px-3 py-2.5 text-xs mb-3 flex items-start gap-2">
              <svg className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
              </svg>
              <span>
                <strong>Email changed — please re-scan this new QR code.</strong> A new authenticator key was generated. Delete any previous entry in your app and scan this updated code.
              </span>
            </div>
          )}

          {/* App links */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
            <p className="text-xs font-semibold text-amber-800 text-center mb-2">Don't have it yet? Download first:</p>
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <a href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-black text-white px-3 py-2 rounded-lg hover:bg-gray-800 transition-colors text-xs font-medium">
                <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M3.18 23.76c.3.17.65.19.98.07l12.49-7.2-2.78-2.78L3.18 23.76zM.54 1.96C.2 2.3 0 2.84 0 3.55v16.9c0 .71.2 1.25.54 1.59l.08.08 9.46-9.46v-.22L.62 1.88l-.08.08zM20.3 10.37l-2.68-1.55-3.01 3.01 3.01 3.01 2.7-1.56c.77-.45.77-1.46-.02-1.91zM4.16.24L16.65 7.43l-2.78 2.78L4.16.24z"/></svg>
                Google Play
              </a>
              <a href="https://apps.apple.com/us/app/google-authenticator/id388497605" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-black text-white px-3 py-2 rounded-lg hover:bg-gray-800 transition-colors text-xs font-medium">
                <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98l-.09.06c-.22.14-2.1 1.22-2.08 3.65.03 2.89 2.54 3.85 2.56 3.86l-.13.11zM13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
                App Store
              </a>
            </div>
          </div>

          <div className="flex justify-center mb-3">
            <div className="p-2 bg-white rounded-xl border border-gray-200 shadow-sm">
              <img src={qrCodeUrl} alt="2FA QR Code" className="w-44 h-44" />
            </div>
          </div>
          <p className="text-xs text-gray-500 text-center mb-4">
            Open Google Authenticator, tap <strong className="text-gray-700">+</strong> then <strong className="text-gray-700">Scan a QR code</strong>.
          </p>

          <label className="block text-xs font-semibold text-gray-700 mb-1 text-center">
            Enter the 6-digit code from your authenticator app
          </label>
          <p className="text-xs text-amber-600 text-center mb-2">Codes expire every 30 seconds — enter a fresh code right before clicking Sign Up.</p>
          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={twoFACode}
            onChange={e => { setTwoFACode(e.target.value.replace(/\D/g, '')); setTwoFAError(''); }}
            placeholder="000000"
            className={`block w-full text-center text-2xl font-mono tracking-[0.4em] rounded-xl border-2 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#40E0D0] bg-white text-gray-900 transition-colors ${twoFACode.length === 6 ? 'border-green-400' : 'border-gray-300 focus:border-[#40E0D0]'}`}
          />
          {twoFACode.length === 6 && (
            <p className="text-green-600 text-xs mt-1.5 text-center flex items-center justify-center gap-1">
              <Shield className="w-3 h-3" /> Code ready — click Sign Up to complete
            </p>
          )}
        </div>
      )}
    </div>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // Success screen
  // ─────────────────────────────────────────────────────────────────────────
  if (submitStatus === 'success' && userType === 'consultant') {
    return (
      <div className="min-h-screen bg-white text-gray-900">
        <Navbar />
        <div className="max-w-lg mx-auto px-4 py-40 space-y-6 text-center">
          <div className="bg-green-50 border border-green-200 rounded-2xl p-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-2xl font-bold text-green-700 mb-2">Account Created!</h3>
            <p className="text-gray-600 mb-2">Your consultant account for <strong>{submittedEmail}</strong> has been created successfully.</p>
            <p className="text-gray-500 text-sm">Redirecting you to login...</p>
          </div>
          <Link to="/consultant/login" className="inline-flex items-center justify-center w-full bg-[#40E0D0] text-black font-semibold px-6 py-3 rounded-xl hover:bg-[#40E0D0]/90 transition-colors">
            Go to Login Now
          </Link>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Main render
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-white text-gray-900 relative">
      <Navbar />

      {/* Header hero */}
      {userType === 'consultant' ? (
        <div className="relative bg-black pt-32 pb-0 overflow-hidden">
          {/* Radial glow blobs */}
          <div className="absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full bg-[#40E0D0]/6 blur-[120px] pointer-events-none" />
          <div className="absolute top-20 right-0 w-[400px] h-[400px] rounded-full bg-[#0066FF]/8 blur-[100px] pointer-events-none" />
          {/* Grid overlay */}
          <div className="absolute inset-0 pointer-events-none" style={{backgroundImage:'linear-gradient(rgba(64,224,208,0.035) 1px,transparent 1px),linear-gradient(90deg,rgba(64,224,208,0.035) 1px,transparent 1px)',backgroundSize:'48px 48px'}} />

          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <button
              onClick={() => navigate('/workday-services')}
              className="flex items-center text-gray-500 hover:text-[#40E0D0] mb-10 transition-colors text-sm group w-fit"
            >
              <ArrowLeft className="w-4 h-4 mr-1.5 group-hover:-translate-x-1 transition-transform" />
              Back to Workday Services
            </button>

            {/* === TOP SECTION: Headline + subtitle === */}
            <div className="text-center mb-14">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#40E0D0]/15 to-[#0066FF]/15 border border-[#40E0D0]/25 text-[#40E0D0] text-xs font-bold px-5 py-2 rounded-full tracking-widest uppercase mb-6">
                <Briefcase className="w-3.5 h-3.5" />
                PerplexityATS JobSeeker Network
              </div>
              <h1 className="text-5xl sm:text-6xl font-extrabold leading-tight mb-6">
                <span className="bg-gradient-to-br from-white via-gray-100 to-gray-300 bg-clip-text text-transparent">Create Your</span>
                <br />
                <span className="bg-gradient-to-r from-[#40E0D0] to-[#0066FF] bg-clip-text text-transparent">Account</span>
              </h1>
              <p className="text-gray-300 text-lg max-w-2xl mx-auto leading-relaxed">
                Join the <span className="text-white font-semibold">PerplexityATS JobSeeker network</span> as a Workday Professional &amp; Consultant — and become part of the community built for people who live and breathe Workday.
              </p>
            </div>

            {/* === COMMUNITY CONNECT FEATURE SECTION === */}
            <div className="relative mb-0">
              {/* Section header */}
              <div className="flex items-center gap-4 mb-8 justify-center">
                <div className="h-px flex-1 max-w-[120px] bg-gradient-to-r from-transparent to-[#40E0D0]/40" />
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#40E0D0] to-[#0066FF] flex items-center justify-center">
                    <Hash className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-white font-bold text-lg tracking-tight">Community Connect</span>
                  <span className="bg-[#40E0D0]/15 text-[#40E0D0] text-xs font-bold px-2.5 py-0.5 rounded-full border border-[#40E0D0]/25">Included Free</span>
                </div>
                <div className="h-px flex-1 max-w-[120px] bg-gradient-to-l from-transparent to-[#40E0D0]/40" />
              </div>

              <p className="text-center text-gray-400 text-sm max-w-xl mx-auto mb-10">
                The social media platform built exclusively for the Workday ecosystem — share, connect, and grow with professionals who speak your language.
              </p>

              {/* Feature cards grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {[
                  {
                    icon: <Zap className="w-5 h-5" />,
                    title: 'Share Insights',
                    desc: 'Post configuration tips, hard-to-find discoveries, and Workday best practices with the community.',
                    color: 'from-[#40E0D0] to-[#00b4d8]',
                    glow: 'rgba(64,224,208,0.15)',
                  },
                  {
                    icon: <MessageSquare className="w-5 h-5" />,
                    title: 'Ask & Answer',
                    desc: 'Post questions, get real answers from experienced Workday professionals — no more searching alone.',
                    color: 'from-[#0066FF] to-[#0099ff]',
                    glow: 'rgba(0,102,255,0.15)',
                  },
                  {
                    icon: <Mic className="w-5 h-5" />,
                    title: 'Start a Podcast',
                    desc: 'Launch your own Workday podcast or share audio content directly with fellow consultants.',
                    color: 'from-[#40E0D0] to-[#0066FF]',
                    glow: 'rgba(64,224,208,0.12)',
                  },
                  {
                    icon: <Users className="w-5 h-5" />,
                    title: 'Connect & Follow',
                    desc: 'Follow top Workday professionals, build your network, and grow your visibility in the ecosystem.',
                    color: 'from-[#0066FF] to-[#40E0D0]',
                    glow: 'rgba(0,102,255,0.12)',
                  },
                ].map(({ icon, title, desc, color, glow }) => (
                  <div key={title} className="group relative rounded-2xl border border-white/8 bg-white/[0.03] hover:bg-white/[0.06] hover:border-white/15 transition-all duration-300 p-5 overflow-hidden">
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl" style={{background:`radial-gradient(ellipse at top left, ${glow}, transparent 70%)`}} />
                    <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center mb-4 text-white flex-shrink-0`}>
                      {icon}
                    </div>
                    <p className="text-white font-semibold text-sm mb-2">{title}</p>
                    <p className="text-gray-500 text-xs leading-relaxed">{desc}</p>
                  </div>
                ))}
              </div>

              {/* Messaging highlight row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-0">
                {/* 1-on-1 Chat */}
                <div className="group relative rounded-2xl border border-[#40E0D0]/20 bg-gradient-to-br from-[#40E0D0]/8 to-transparent p-5 overflow-hidden hover:border-[#40E0D0]/40 transition-all duration-300">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#40E0D0] to-[#00b4d8] flex items-center justify-center flex-shrink-0 shadow-lg shadow-[#40E0D0]/20">
                      <MessageCircle className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-white font-bold text-sm mb-1">1-on-1 Direct Messaging</p>
                      <p className="text-gray-400 text-xs leading-relaxed">Have private conversations with any consultant in your network. Collaborate, refer opportunities, or simply connect one-on-one.</p>
                    </div>
                  </div>
                </div>

                {/* Group Chat */}
                <div className="group relative rounded-2xl border border-[#0066FF]/20 bg-gradient-to-br from-[#0066FF]/8 to-transparent p-5 overflow-hidden hover:border-[#0066FF]/40 transition-all duration-300">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#0066FF] to-[#0099ff] flex items-center justify-center flex-shrink-0 shadow-lg shadow-[#0066FF]/20">
                      <Radio className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-white font-bold text-sm mb-1">Group Chats &amp; Channels</p>
                      <p className="text-gray-400 text-xs leading-relaxed">Create or join group chats for your team, niche workstream, or project. Collaborate in real time with multiple professionals at once.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bottom tagline strip */}
              <div className="mt-6 py-4 border-t border-white/6 text-center">
                <p className="text-gray-600 text-xs tracking-wide">Perplexity's official Workday community platform &nbsp;·&nbsp; Share your Work-Day with the world</p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-black pt-32 pb-12">
          <div className="max-w-3xl mx-auto px-4 text-center">
            <button
              onClick={() => navigate('/workday-services')}
              className="flex items-center text-gray-400 hover:text-white mb-6 transition-colors text-sm group mx-auto w-fit"
            >
              <ArrowLeft className="w-4 h-4 mr-1.5 group-hover:-translate-x-1 transition-transform" />
              Back to Workday Services
            </button>
            <h1 className="text-3xl sm:text-4xl font-bold mb-4 bg-gradient-to-r from-[#40E0D0] to-[#0066FF] bg-clip-text text-transparent">
              Create Your Account
            </h1>
            <p className="text-gray-400 text-sm max-w-lg mx-auto mb-8">
              Join the PerplexityATS network as a Workday Customer.
            </p>
          </div>
        </div>
      )}

      {/* ── CLIENT SIGNUP ──────────────────────────────────────────────────── */}
      {userType === 'client' && (
        <div className="max-w-2xl mx-auto px-4 py-12">

          {/* Plan selector */}
          <div className="mb-8">
            <h2 className="text-lg font-bold text-gray-800 mb-4 text-center">Choose Your Plan</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

              {/* Free plan card */}
              <button
                type="button"
                onClick={() => setClientPlan('free')}
                className={`text-left rounded-2xl border-2 p-5 transition-all ${clientPlan === 'free' ? 'border-[#40E0D0] bg-[#40E0D0]/5' : 'border-gray-200 bg-white hover:border-gray-400'}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <span className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-1 block">Free</span>
                    <span className="text-2xl font-extrabold text-gray-900">$0</span>
                    <span className="text-gray-500 text-sm ml-1">/mo</span>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-1 transition-colors ${clientPlan === 'free' ? 'border-[#40E0D0] bg-[#40E0D0]' : 'border-gray-300'}`}>
                    {clientPlan === 'free' && <Check className="w-3 h-3 text-black" />}
                  </div>
                </div>
                <ul className="space-y-1.5">
                  {['2 team seats', 'Browse talent pool', 'Interview requests via Perplexity', 'Job posting via Perplexity team'].map(f => (
                    <li key={f} className="flex items-center gap-2 text-xs text-gray-600">
                      <CheckCircle className="w-3.5 h-3.5 text-[#40E0D0] flex-shrink-0" />{f}
                    </li>
                  ))}
                </ul>
              </button>

              {/* Pro plan card */}
              <button
                type="button"
                onClick={() => setClientPlan('pro')}
                className={`text-left rounded-2xl border-2 p-5 transition-all ${clientPlan === 'pro' ? 'border-[#40E0D0] bg-[#40E0D0]/5' : 'border-gray-200 bg-white hover:border-gray-400'}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-bold uppercase tracking-widest text-[#0099aa]">Pro</span>
                      <span className="inline-flex items-center gap-1 bg-[#40E0D0]/10 border border-[#40E0D0]/30 text-[#0099aa] text-[10px] font-bold px-2 py-0.5 rounded-full">
                        <Star className="w-2.5 h-2.5" /> Most Popular
                      </span>
                    </div>
                    <span className="text-2xl font-extrabold text-gray-900">$499</span>
                    <span className="text-gray-500 text-sm ml-1">/mo</span>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-1 transition-colors ${clientPlan === 'pro' ? 'border-[#40E0D0] bg-[#40E0D0]' : 'border-gray-300'}`}>
                    {clientPlan === 'pro' && <Check className="w-3 h-3 text-black" />}
                  </div>
                </div>
                <ul className="space-y-1.5">
                  {['3 included seats', 'Self-service job posting', 'Direct candidate messaging', 'Full talent pool access', 'Pay via PayPal after signup'].map(f => (
                    <li key={f} className="flex items-center gap-2 text-xs text-gray-600">
                      <CheckCircle className={`w-3.5 h-3.5 flex-shrink-0 ${f.includes('PayPal') ? 'text-amber-500' : 'text-[#40E0D0]'}`} />{f}
                    </li>
                  ))}
                </ul>
              </button>
            </div>

            {clientPlan === 'pro' && (
              <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-3">
                <Zap className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800">
                  <strong>Pro — PayPal checkout next:</strong> Create your account below, then you'll be taken directly to a secure PayPal checkout to activate your Pro subscription (Monthly $499/mo or Yearly $5,988/yr).
                </p>
              </div>
            )}
          </div>

          {/* Client form */}
          <form onSubmit={handleClientSubmit} className="space-y-5">

            {/* Contact info */}
            <div className="bg-white border-2 border-gray-200 rounded-xl p-6 shadow-sm">
              <h3 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Building2 className="w-4 h-4 text-[#40E0D0]" /> Contact Information
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">First Name *</label>
                  <input type="text" name="first_name" required value={clForm.first_name} onChange={handleClChange} className={inputCls} placeholder="Jane" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Last Name *</label>
                  <input type="text" name="last_name" required value={clForm.last_name} onChange={handleClChange} className={inputCls} placeholder="Smith" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email *</label>
                  <input type="email" name="email" required value={clForm.email} onChange={handleClChange} className={inputCls} placeholder="jane@company.com" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Phone *</label>
                  <input type="tel" name="phone" required value={clForm.phone} onChange={e => setClForm(p => ({ ...p, phone: formatPhone(e.target.value) }))} placeholder="(555) 000-0000" maxLength={14} className={inputCls} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Company *</label>
                  <input type="text" name="company" required value={clForm.company} onChange={handleClChange} className={inputCls} placeholder="Acme Corp" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Your Title *</label>
                  <input type="text" name="title" required value={clForm.title} onChange={handleClChange} className={inputCls} placeholder="HR Director" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Country / Region *</label>
                  <input type="text" name="country_region" required value={clForm.country_region} onChange={handleClChange} className={inputCls} placeholder="United States" />
                </div>
              </div>
            </div>

            {/* Org details */}
            <div className="bg-white border-2 border-gray-200 rounded-xl p-6 shadow-sm">
              <h3 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Users className="w-4 h-4 text-[#40E0D0]" /> Organization Details
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Company Size *</label>
                  <select name="company_size" required value={clForm.company_size} onChange={handleClChange} className={inputCls}>
                    <option value="">Select size</option>
                    <option value="1-50">1–50 employees</option>
                    <option value="51-200">51–200 employees</option>
                    <option value="201-1000">201–1,000 employees</option>
                    <option value="1000+">1,000+ employees</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Project Timeline *</label>
                  <select name="timeline" required value={clForm.timeline} onChange={handleClChange} className={inputCls}>
                    <option value="">Select timeline</option>
                    <option value="immediate">Immediate</option>
                    <option value="1-month">Within 1 month</option>
                    <option value="3-months">Within 3 months</option>
                    <option value="ongoing">Ongoing service needs</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Budget Range (per hour)</label>
                  <input type="text" name="budget_range" value={clForm.budget_range} onChange={handleClChange} className={inputCls} placeholder="e.g. $75–$125/hr" />
                </div>
              </div>
            </div>

            {/* Account setup */}
            <div className="bg-white border-2 border-gray-200 rounded-xl p-6 shadow-sm">
              <h3 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Shield className="w-4 h-4 text-[#40E0D0]" /> Account Setup
              </h3>

              {emailConflict && (
                <div className="bg-amber-50 border-2 border-amber-400 rounded-xl p-4 mb-4">
                  <p className="text-amber-900 font-bold text-sm mb-1">An active account already exists for this email address.</p>
                  <p className="text-amber-800 text-sm mb-3">For security, we cannot create a new account using an email that is already registered. Please log in to your existing account.</p>
                  <div className="flex flex-wrap gap-2">
                    <a href="/customer/login" className="inline-flex items-center gap-1.5 bg-amber-600 text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-amber-700 transition-colors">
                      Log in to your account
                    </a>
                    <a href="/customer/login#reset" className="inline-flex items-center gap-1.5 bg-white border border-amber-400 text-amber-800 text-sm font-medium px-4 py-2 rounded-lg hover:bg-amber-50 transition-colors">
                      Forgot password?
                    </a>
                  </div>
                </div>
              )}

              {!emailConflict && formError && (
                <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg mb-4 text-sm">
                  {formError}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Password *</label>
                  <div className="relative mt-1">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      required
                      value={clForm.password}
                      onChange={handleClChange}
                      className="block w-full rounded-md bg-white border border-gray-300 text-gray-900 px-4 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent"
                      placeholder="Min. 6 characters"
                    />
                    <button type="button" onClick={() => setShowPassword(v => !v)} className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Confirm Password *</label>
                  <div className="relative mt-1">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      required
                      value={clForm.confirmPassword}
                      onChange={handleClChange}
                      className="block w-full rounded-md bg-white border border-gray-300 text-gray-900 px-4 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent"
                      placeholder="Re-enter password"
                    />
                    <button type="button" onClick={() => setShowConfirmPassword(v => !v)} className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600">
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {clForm.confirmPassword.length > 0 && (
                    <p className={`text-xs mt-1 ${clForm.password === clForm.confirmPassword ? 'text-green-600' : 'text-red-600'}`}>
                      {clForm.password === clForm.confirmPassword ? '✓ Passwords match' : 'Passwords do not match'}
                    </p>
                  )}
                </div>
              </div>

              {twoFABlock}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-[#40E0D0] to-[#0066FF] text-white font-bold px-6 py-3.5 rounded-xl inline-flex items-center justify-center gap-2 transition-all hover:scale-[1.01] shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {isSubmitting ? (
                <><Loader2 className="animate-spin w-4 h-4" /> Creating Account...</>
              ) : clientPlan === 'pro' ? (
                <><Briefcase className="w-4 h-4" /> Create Pro Account &amp; Continue to PayPal</>
              ) : (
                <><Send className="w-4 h-4" /> Create Free Account</>
              )}
            </button>
            <p className="text-center text-gray-400 text-xs">
              By signing up you agree to our{' '}
              <a href="/terms" className="text-[#0099aa] hover:underline">Terms of Service</a> and{' '}
              <a href="/privacy" className="text-[#0099aa] hover:underline">Privacy Policy</a>.
            </p>
            <p className="text-center text-gray-500 text-sm">
              Already have an account?{' '}
              <Link to="/customer/login" className="text-[#40E0D0] hover:text-[#0066FF] transition-colors font-medium">Log in</Link>
            </p>
          </form>
        </div>
      )}

      {/* ── CONSULTANT SIGNUP ───────────────────────────────────────────────── */}
      {userType === 'consultant' && (
        <div className="max-w-4xl mx-auto px-4 py-12">

          <div className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-xl p-6 text-center shadow-sm mb-8">
            <MessageSquare className="w-8 h-8 text-[#40E0D0] mx-auto mb-3" />
            <h2 className="text-2xl font-bold mb-2 bg-gradient-to-r from-[#40E0D0] to-[#0066FF] bg-clip-text text-transparent">
              Join Our Workday Job-Seekers Network
            </h2>
            <p className="text-gray-600 text-sm">
              Connect with leading organizations looking for Workday experts across all modules.
            </p>
          </div>

          <form onSubmit={handleConsultantSubmit} className="space-y-6">

            {/* Basic Information */}
            <div className="bg-white border-2 border-gray-300 rounded-xl p-6 shadow-lg">
              <h3 className="text-xl font-bold mb-4 text-gray-900">Basic Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">First Name *</label>
                  <input type="text" name="first_name" required value={cForm.first_name} onChange={handleCChange} className={inputCls} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Last Name *</label>
                  <input type="text" name="last_name" required value={cForm.last_name} onChange={handleCChange} className={inputCls} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email *</label>
                  <input type="email" name="email" required value={cForm.email} onChange={handleCChange} className={inputCls} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Phone Number *</label>
                  <input
                    type="tel" name="phone" required value={cForm.phone}
                    onChange={e => setCForm(p => ({ ...p, phone: formatPhone(e.target.value) }))}
                    placeholder="(555) 123-4567" maxLength={14} className={inputCls}
                  />
                  <p className="text-xs text-gray-400 mt-1">Format: (555) 123-4567</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Current Company</label>
                  <select name="company_status" value={cForm.company_status} onChange={handleCChange} className={inputCls}>
                    <option value="">Enter company name or select status...</option>
                    <option value="available_public">Available for Work — Public</option>
                    <option value="available_private">Available to Work — Private</option>
                    <option value="employed">Currently Employed (enter name below)</option>
                  </select>
                  {cForm.company_status === 'employed' && (
                    <input type="text" name="company" value={cForm.company} onChange={handleCChange} placeholder="Enter your company name" className={`${inputCls} mt-2`} />
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Current Title</label>
                  <p className="text-xs text-gray-400 mb-1">Enter a title representing your experience level or the role you're seeking.</p>
                  <input type="text" name="title" value={cForm.title} onChange={handleCChange} placeholder="e.g. Senior Workday HCM Consultant" className={inputCls} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Zip Code *</label>
                  <input type="text" name="zip_code" required value={cForm.zip_code} onChange={handleCChange} placeholder="e.g. 75024" maxLength={10} className={inputCls} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">City</label>
                  <input type="text" name="city" value={cForm.city} onChange={handleCChange} placeholder="e.g. Dallas" className={inputCls} />
                </div>
                {showStateDropdown ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">{cForm.country === 'Canada' ? 'Province' : 'State'}</label>
                    <select name="state" value={cForm.state} onChange={handleCChange} className={inputCls}>
                      <option value="">Select {cForm.country === 'Canada' ? 'province' : 'state'}...</option>
                      {stateOptions.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">State / Region</label>
                    <input type="text" name="state" value={cForm.state} onChange={handleCChange} placeholder="State or region" className={inputCls} />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Country *</label>
                  <select name="country" required value={cForm.country} onChange={handleCChange} className={inputCls}>
                    {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* Workday Experience */}
            <div className="bg-white border-2 border-gray-300 shadow-lg rounded-xl p-6">
              <h3 className="text-xl font-bold mb-4 text-gray-900">Workday Experience</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Years of Workday Experience *</label>
                  <select name="experience_years" required value={cForm.experience_years} onChange={handleCChange} className={inputCls}>
                    <option value="">Select experience</option>
                    <option value="1-2">1-2 years</option>
                    <option value="3-5">3-5 years</option>
                    <option value="6-10">6-10 years</option>
                    <option value="10+">10+ years</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Availability *</label>
                  <select name="availability" required value={cForm.availability} onChange={handleCChange} className={inputCls}>
                    <option value="">Select availability</option>
                    <option value="Immediate">Immediate</option>
                    <option value="2 Weeks Notice">2 Weeks Notice</option>
                    <option value="1 Month Notice">1 Month Notice</option>
                    <option value="Flexible">Flexible</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <MultiSelectDropdown
                    label="Workday Modules & Functional Expertise"
                    options={WORKDAY_MODULES}
                    selected={cForm.workday_modules_list}
                    onChange={val => setCForm(p => ({ ...p, workday_modules_list: val }))}
                    placeholder="Click to select modules..."
                    required
                  />
                  <p className="text-xs text-gray-400 mt-1">Select all that apply.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Preferred Hourly Rate Range *</label>
                  <input type="text" name="hourly_rate" required value={cForm.hourly_rate} onChange={handleCChange} placeholder="e.g., $75-100/hour" className={inputCls} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Workday Certifications, Training Certificates or Courses</label>
                  <textarea name="certifications" value={cForm.certifications} onChange={handleCChange} rows={3} placeholder="List your Workday certifications..." className={`${inputCls} resize-none`} />
                </div>
              </div>
            </div>

            {/* Project Preferences */}
            <div className="bg-white border-2 border-gray-300 shadow-lg rounded-xl p-6">
              <h3 className="text-xl font-bold mb-4 text-gray-900">Workday Position &amp; Project Preferences</h3>
              <div className="space-y-6">
                <MultiSelectDropdown
                  label="Select your Workday Position & Project Preferences"
                  options={PROJECT_PREFERENCES}
                  selected={cForm.project_preferences_list}
                  onChange={val => setCForm(p => ({ ...p, project_preferences_list: val }))}
                  placeholder="Click to select positions & project types..."
                />
                <p className="text-xs text-gray-400 -mt-4">Select all that apply.</p>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Additional Details About Your Project Preferences</label>
                  <textarea name="message" value={cForm.message} onChange={handleCChange} rows={3} className={`${inputCls} resize-none`} placeholder="Any additional details about your preferred project types or working style..." />
                </div>

                {/* Resume Upload */}
                <div className="border-t border-gray-100 pt-5">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Upload Resume <span className="text-gray-400 text-xs">(Optional — appears on your profile)</span>
                  </label>
                  {cForm.resume_file ? (
                    <div className="flex items-center gap-3 bg-[#40E0D0]/5 border border-[#40E0D0]/30 rounded-lg px-4 py-3">
                      <Upload className="w-5 h-5 text-[#40E0D0]" />
                      <span className="text-sm font-medium text-gray-800 flex-1 truncate">{cForm.resume_file.name}</span>
                      <button type="button" onClick={() => setCForm(p => ({ ...p, resume_file: null }))} className="text-gray-400 hover:text-red-500 transition-colors">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <label className="bg-[#40E0D0] text-black px-4 py-2 rounded-lg inline-flex items-center cursor-pointer hover:bg-[#40E0D0]/90 transition-colors font-semibold">
                      <Upload className="h-5 w-5 mr-2" />
                      Choose Resume File
                      <input type="file" className="hidden" accept=".pdf,.doc,.docx" onChange={e => setCForm(p => ({ ...p, resume_file: e.target.files?.[0] || null }))} />
                    </label>
                  )}
                  <p className="text-xs text-gray-400 mt-2">Supported formats: PDF, DOC, DOCX (Max 5MB)</p>
                </div>
              </div>
            </div>

            {/* Account Setup */}
            <div className="bg-white border-2 border-gray-300 shadow-lg rounded-xl p-6">
              <h3 className="text-xl font-bold mb-4 text-gray-900">Account Setup</h3>

              {emailConflict && (
                <div className="bg-amber-50 border-2 border-amber-400 rounded-xl p-4 mb-4">
                  <p className="text-amber-900 font-bold text-sm mb-1">An active account already exists for this email address.</p>
                  <p className="text-amber-800 text-sm mb-3">For security, we cannot create a new account using an email that is already registered. Please log in to your existing account.</p>
                  <div className="flex flex-wrap gap-2">
                    <a href="/consultant/login" className="inline-flex items-center gap-1.5 bg-amber-600 text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-amber-700 transition-colors">
                      Log in to your account
                    </a>
                    <a href="/consultant/login#reset" className="inline-flex items-center gap-1.5 bg-white border border-amber-400 text-amber-800 text-sm font-medium px-4 py-2 rounded-lg hover:bg-amber-50 transition-colors">
                      Forgot password?
                    </a>
                  </div>
                </div>
              )}

              {!emailConflict && formError && (
                <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg mb-4 text-sm">
                  {formError}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Password *</label>
                  <div className="relative mt-1">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      required
                      value={cForm.password}
                      onChange={handleCChange}
                      className="block w-full rounded-md bg-white border border-gray-300 text-gray-900 px-4 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent"
                      placeholder="Min. 6 characters"
                    />
                    <button type="button" onClick={() => setShowPassword(v => !v)} className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700">
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Confirm Password *</label>
                  <div className="relative mt-1">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      required
                      value={cForm.confirmPassword}
                      onChange={handleCChange}
                      className="block w-full rounded-md bg-white border border-gray-300 text-gray-900 px-4 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent"
                      placeholder="Confirm your password"
                    />
                    <button type="button" onClick={() => setShowConfirmPassword(v => !v)} className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700">
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
              </div>

              {twoFABlock}
            </div>

            <div className="flex items-center justify-end gap-4">
              {submitStatus === 'error' && <span className="text-red-600 text-sm">Failed to create account. Please try again.</span>}
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-[#40E0D0] text-black font-semibold px-8 py-3 rounded-lg inline-flex items-center hover:bg-[#40E0D0]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? <><Loader2 className="animate-spin mr-2" /> Creating Account...</> : <><Send className="mr-2" /> Sign Up</>}
              </button>
            </div>

            <p className="text-center text-gray-500 text-sm">
              Already have an account?{' '}
              <Link to="/consultant/login" className="text-[#40E0D0] hover:text-[#0066FF] transition-colors font-medium">Log in</Link>
            </p>
          </form>
        </div>
      )}

      <AuthPlatformNotice />

      {/* Footer */}
      <div className="bg-black py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div>
              <img src={logoUrl} alt="Perplexity" className="h-12 w-auto mb-4" />
              <p className="text-gray-400 text-sm">A Registered Trademark of Perplexity Solved Solutions Inc</p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Contact</h4>
              <div className="space-y-2">
                <a href="mailto:info@perplexityonline.com" className="text-gray-400 hover:text-white flex items-center gap-2 text-sm">info@perplexityonline.com</a>
                <a href="tel:18663078708" className="text-gray-400 hover:text-white flex items-center gap-2 text-sm">1 (866) 307-8708</a>
              </div>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Address</h4>
              <p className="text-gray-400 text-sm">Headquarters<br />5851 Legacy Cir<br />Plano, Texas 75024</p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Follow Us</h4>
              <div className="flex space-x-4">
                <a href="https://www.linkedin.com/company/perplexityhq/" target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg bg-gradient-to-br from-[#0077B5] to-[#00A0DC] hover:scale-110 transition-transform">
                  <span className="w-5 h-5 text-white block text-xs font-bold">in</span>
                </a>
                <a href="https://x.com/PerplexityHQ" target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg bg-gradient-to-br from-[#000000] to-[#333333] hover:scale-110 transition-transform">
                  <span className="w-5 h-5 text-white block text-xs font-bold">X</span>
                </a>
                <a href="https://www.facebook.com/Perplexitymeet/" target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg bg-gradient-to-br from-[#1877F2] to-[#00B2FF] hover:scale-110 transition-transform">
                  <span className="w-5 h-5 text-white block text-xs font-bold">f</span>
                </a>
              </div>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">PERPLEXITY™, all rights reserved.</p>
            <div className="flex items-center space-x-6 mt-4 md:mt-0">
              <a href="/terms" className="text-[#40E0D0] hover:text-[#0066FF] text-sm transition-colors">Terms of Service</a>
              <a href="/privacy" className="text-[#40E0D0] hover:text-[#0066FF] text-sm transition-colors">Privacy Statement</a>
            </div>
          </div>
        </div>
      </div>

      {/* Pro Plan Confirmation Modal */}
      {showProConfirmModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => { if (!isSubmitting) setShowProConfirmModal(false); }}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 z-10">
            {!isSubmitting && (
              <button
                type="button"
                onClick={() => setShowProConfirmModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            )}

            <div className="flex items-center gap-3 mb-4">
              <div className="bg-[#40E0D0]/10 border border-[#40E0D0]/30 rounded-full p-2.5 flex-shrink-0">
                <Lock className="w-5 h-5 text-[#40E0D0]" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">Before you continue to PayPal</h2>
                <p className="text-xs text-gray-500 mt-0.5">Review what happens next</p>
              </div>
            </div>

            <p className="text-sm text-gray-600 leading-relaxed mb-2">
              Clicking <strong className="text-gray-800">"Yes, create my Pro account"</strong> will:
            </p>
            <ul className="text-sm text-gray-600 space-y-1.5 mb-4 pl-1">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-[#40E0D0] flex-shrink-0 mt-0.5" />
                Create your PerplexityATS account with 2FA enabled
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-[#40E0D0] flex-shrink-0 mt-0.5" />
                Redirect you to PayPal to complete the $499/mo subscription
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-[#40E0D0] flex-shrink-0 mt-0.5" />
                Unlock full Pro features immediately after payment is confirmed
              </li>
            </ul>

            <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 text-xs text-amber-700 mb-5">
              If you don't complete payment after being redirected, your account will be created as a <strong>Free account</strong> automatically.
            </div>

            <div className="flex flex-col gap-3">
              <button
                type="button"
                disabled={isSubmitting}
                onClick={handleConfirmPro}
                className="w-full bg-gradient-to-r from-[#40E0D0] to-[#0066FF] text-white font-bold px-6 py-3 rounded-xl inline-flex items-center justify-center gap-2 transition-all hover:scale-[1.02] shadow-md disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100 text-sm"
              >
                {isSubmitting ? (
                  <><Loader2 className="animate-spin w-4 h-4" /> Creating your account...</>
                ) : (
                  <><Send className="w-4 h-4" /> Yes, create my Pro account — $499/mo</>
                )}
              </button>

              <button
                type="button"
                disabled={isSubmitting}
                onClick={handleConfirmFree}
                className="w-full border border-gray-200 text-gray-700 font-medium px-6 py-2.5 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm inline-flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <><Loader2 className="animate-spin w-4 h-4" /> Creating free account...</>
                ) : (
                  <>No thanks, just create a free account</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
